// Keyboard/mouse + Gamepad API (standard mapping, PlayStation labels). Produces a per-frame intent.
export type Action = 'interact' | 'reload' | 'ability' | 'throw' | 'cycleThrow' | 'hack' | 'inventory' | 'map' | 'chat' | 'quickchat' | 'ping' | 'radio' | 'zoom'
  | 'slot0' | 'slot1' | 'slot2' | 'slot3' | 'nextWeapon' | 'hack1' | 'hack2' | 'hack3' | 'escape' | 'respawn'
  | 'menuUp' | 'menuDown' | 'menuLeft' | 'menuRight' | 'menuOk' | 'menuBack' | 'tabPrev' | 'tabNext';

export class Input {
  keys = new Set<string>();
  mouseX = 320; mouseY = 180; // in 640x360 view space
  mouseDown = false;
  pressed: Action[] = [];
  gamepad = false;
  padAim = { x: 0, y: 0 }; padMove = { x: 0, y: 0 }; padFire = false; padThrottle = 0; padBrake = 0;
  private prevButtons: boolean[] = [];
  typing = false; // chat/menus with text inputs focused
  constructor(private view: HTMLElement, private toView: (cx: number, cy: number) => [number, number]) {
    addEventListener('keydown', e => {
      if (this.typing) return;
      if (e.code === 'Tab') e.preventDefault();
      if (!e.repeat) this.keyAction(e.code);
      this.keys.add(e.code);
    });
    addEventListener('keyup', e => this.keys.delete(e.code));
    addEventListener('blur', () => { this.keys.clear(); this.mouseDown = false; });
    view.addEventListener('mousemove', e => { [this.mouseX, this.mouseY] = this.toView(e.clientX, e.clientY); this.gamepad = false; });
    view.addEventListener('mousedown', e => {
      if (e.button === 0) this.mouseDown = true;
      if (e.button === 1) { e.preventDefault(); this.pressed.push('ping'); }
    });
    addEventListener('mouseup', e => { if (e.button === 0) this.mouseDown = false; });
    view.addEventListener('contextmenu', e => e.preventDefault());
    view.addEventListener('auxclick', e => e.preventDefault());
  }
  private keyAction(code: string) {
    const map: Record<string, Action> = {
      KeyE: 'interact', KeyR: 'reload', Space: 'ability', KeyG: 'throw', KeyQ: 'hack', Tab: 'inventory', KeyM: 'map', KeyT: 'cycleThrow', KeyY: 'chat',
      KeyN: 'radio', KeyZ: 'zoom', Digit1: 'hack1', Digit2: 'hack2', Digit3: 'hack3', Escape: 'escape', Enter: 'respawn',
      KeyF: 'nextWeapon', Backquote: 'slot0', Digit4: 'slot0',
    };
    const a = map[code]; if (a) this.pressed.push(a);
  }
  axis() {
    const k = this.keys;
    let mx = (k.has('KeyD') || k.has('ArrowRight') ? 1 : 0) - (k.has('KeyA') || k.has('ArrowLeft') ? 1 : 0);
    let my = (k.has('KeyS') || k.has('ArrowDown') ? 1 : 0) - (k.has('KeyW') || k.has('ArrowUp') ? 1 : 0);
    if (this.gamepad) { mx = this.padMove.x; my = this.padMove.y; }
    return { mx, my };
  }
  /** Driving: throttle from W/S or R2/L2; steer from A/D or left stick X. Returns {mx: steer, my: -throttle}. */
  driveAxis() {
    const a = this.axis();
    if (this.gamepad) return { mx: this.padMove.x, my: -(this.padThrottle - this.padBrake) };
    return a;
  }
  /** Handbrake: Shift, or Square on a gamepad (held). */
  handbrake() { return this.gamepad ? !!this.padButtons[2] : this.keys.has('ShiftLeft') || this.keys.has('ShiftRight'); }
  fire(driving: boolean) { return this.gamepad ? (driving ? this.padButtons[5] : this.padFire) : this.mouseDown; }
  padButtons: boolean[] = [];
  take(): Action[] { const p = this.pressed; this.pressed = []; return p; }

  /** Poll the first standard-mapped gamepad. menuOpen switches face buttons to menu navigation. */
  pollGamepad(menuOpen: boolean, hackMode: boolean, driving: boolean) {
    const gp = navigator.getGamepads?.().find(g => g && g.connected);
    if (!gp) return;
    const b = gp.buttons.map(x => x.pressed);
    const edge = (i: number) => b[i] && !this.prevButtons[i];
    const dz = (v: number) => (Math.abs(v) < 0.18 ? 0 : v);
    const lx = dz(gp.axes[0] ?? 0), ly = dz(gp.axes[1] ?? 0), rx = dz(gp.axes[2] ?? 0), ry = dz(gp.axes[3] ?? 0);
    if (b.some(Boolean) || lx || ly || rx || ry) this.gamepad = true;
    if (!this.gamepad) { this.prevButtons = b; return; }
    this.padMove = { x: lx, y: ly };
    if (rx || ry) this.padAim = { x: rx, y: ry };
    this.padThrottle = gp.buttons[7]?.value ?? 0; this.padBrake = gp.buttons[6]?.value ?? 0;
    this.padFire = b[7] && !driving || (b[5] ?? false);
    this.padButtons = b;
    const P = (a: Action) => this.pressed.push(a);
    // 0 Cross, 1 Circle, 2 Square, 3 Triangle, 4 L1, 5 R1, 6 L2, 7 R2, 8 Share, 9 Options, 10 L3, 11 R3, 12-15 D-pad, 17 Touchpad
    if (menuOpen) {
      if (edge(12)) P('menuUp'); if (edge(13)) P('menuDown'); if (edge(14)) P('menuLeft'); if (edge(15)) P('menuRight');
      if (edge(0)) P('menuOk'); if (edge(1)) P('menuBack'); if (edge(4)) P('tabPrev'); if (edge(5)) P('tabNext');
      if (edge(9)) P('inventory'); if (edge(8) || edge(17)) P('map');
    } else if (hackMode) {
      if (edge(2)) P('hack1'); if (edge(3)) P('hack2'); if (edge(1)) P('hack3'); if (edge(4)) P('hack');
      if (edge(14)) P('menuLeft'); if (edge(15)) P('menuRight');
      if (edge(0)) P('hack1');
    } else {
      if (edge(0)) P('interact'); if (edge(2)) P('reload'); if (edge(1)) P('ability'); if (edge(3)) P('nextWeapon');
      if (edge(4)) P('hack'); if (edge(6) && !driving) P('throw'); if (edge(11)) P('ping'); if (edge(10)) P(driving ? 'radio' : 'cycleThrow');
      if (edge(9)) P('inventory'); if (edge(8) || edge(17)) P('map');
      if (edge(12)) P('slot1'); if (edge(15)) P('slot2'); if (edge(13)) P('slot3'); if (edge(14)) P('slot0');
      if (edge(16)) P('respawn');
    }
    if (edge(9) && menuOpen) { /* handled above */ }
    this.prevButtons = b;
  }
}
